import * as React from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faStatStripXpress2,
    faV,
    faVitaloJak1,
    faVitaloJak2
} from "../../misc/vitalograph-svg-icons";
import { ScheduleVisit } from "./VisitSchedule";
import { DeviceTypes, SessionType } from "../../misc/api/apiTypes";
import { useVisitScheduleContext } from "./VisitScheduleContext";

const grid = 6;

export const getSessionContainerStyle = (isDraggingOver): React.CSSProperties => ({
    background: isDraggingOver ? "lightblue" : "#f1f2f2",
    padding: `${grid}`,
    width: `100%`,
    display: "flex",
    margin: `${grid}px 0`,
    flexWrap: "wrap"
});

export const getItemStyle = (isDragging, draggableStyle): React.CSSProperties => {
    return {
        userSelect: "none",
        padding: grid * 2,
        margin: `${grid}px ${grid}px`,
        textAlign: "right",
        background: isDragging ? "lightgreen" : "#fff",
        cursor: "pointer",
        display: "flex",

        ...draggableStyle
    };
};

export interface SessionItemsProps {
    visit: ScheduleVisit;
    visitNum: number;
    setActive: (id: string) => void;
    dropDisabled: boolean;
}

export const SessionItems = ({ visit, visitNum, setActive, dropDisabled }: SessionItemsProps) => {
    const { sessionTypes } = useVisitScheduleContext();

    const itemIcon = (deviceType: DeviceTypes) => {
        switch (deviceType) {
            case DeviceTypes.VitaloJAK1:
                return faVitaloJak1;
            case DeviceTypes.VitaloJAK2:
                return faVitaloJak2;
            case DeviceTypes.Pneumotrac:
                return faV;
            case DeviceTypes.StatStripXpress2:
                return faStatStripXpress2;
            case DeviceTypes.P230EDiary:
                return faV;
            default:
                return faV;
        }
    };

    return (
        <Droppable
            droppableId={visit.id}
            direction="horizontal"
            isDropDisabled={dropDisabled}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    style={getSessionContainerStyle(snapshot.isDraggingOver)}
                    onClick={e => e.stopPropagation()}
                >
                    {visit.sessions.map((session, index) => {
                        return (
                            <Draggable
                                key={`${visitNum}${index}`}
                                draggableId={session.id}
                                index={index}
                            >
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style
                                        )}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setActive(session.id);
                                        }}
                                    >
                                        <span
                                            className={
                                                session.disabled ? "text-danger" : "text-primary"
                                            }
                                        >
                                            <div className="row">
                                                <div className="col-md-2">
                                                    <span {...provided.dragHandleProps}>
                                                        <i
                                                            className="fas fa-grip-vertical"
                                                            style={{ float: "left" }}
                                                        />
                                                    </span>
                                                </div>
                                                <div className="col-md-10">
                                                    <h4>
                                                        <FontAwesomeIcon
                                                            icon={itemIcon(session.deviceType)}
                                                            size="lg"
                                                        />
                                                        &nbsp;
                                                        <span data-automationid={session.label}>{session.label}</span>
                                                        <div>
                                                            <small>
                                                                {sessionTypes.length > 0 &&
                                                                    sessionTypes.find(
                                                                        st =>
                                                                            st.Id ===
                                                                            session.sessionType
                                                                    ).Name}
                                                            </small>
                                                        </div>
                                                    </h4>
                                                </div>
                                            </div>
                                        </span>
                                    </div>
                                )}
                            </Draggable>
                        );
                    })}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
};
